"use client";
import * as React from "react";
import { DataTable, type Column } from "../DataTable";
import { PageHeader, StatCard, Card2 } from "../ModuleHelpers";
import { useERP, formatNum } from "@/lib/erp/store";
import { Warehouse } from "lucide-react";

interface OpRow { id: string; ref: string; type: "Inbound" | "Outbound"; warehouse: string; date: string; lines: number; status: string; }

export function WMSModule() {
  const erp = useERP();
  const inbound: OpRow[] = erp.goodsReceipts.map(g => ({
    id: g.id, ref: g.grnNumber, type: "Inbound", warehouse: erp.getWarehouse(g.warehouseId)?.name ?? "—",
    date: g.receivedDate, lines: g.lines.length, status: g.status,
  }));
  const outbound: OpRow[] = erp.salesOrders.filter(s => s.status === "Dispatched" || s.status === "Delivered").map(s => ({
    id: s.id, ref: s.soNumber, type: "Outbound", warehouse: erp.getWarehouse(s.warehouseId)?.name ?? "—",
    date: s.orderDate, lines: s.lines.length, status: s.status,
  }));
  const rows = [...inbound, ...outbound];
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Warehouse Management" desc="Inbound, outbound, picking and dispatch operations." icon={Warehouse} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Warehouses" value={formatNum(erp.warehouses.length)} />
        <StatCard label="Inbound Today" value={formatNum(inbound.length)} />
        <StatCard label="Outbound Today" value={formatNum(outbound.length)} tone="text-primary" />
        <StatCard label="Avg Utilization" value={`${Math.round(erp.warehouses.reduce((s, w) => s + w.utilization, 0) / erp.warehouses.length)}%`} />
      </div>
      <Card2 title="Operations Log">
        <DataTable
          columns={[
            { key: "ref", header: "Reference", sortable: true, render: r => <span className="font-mono text-xs">{r.ref}</span> },
            { key: "type", header: "Type", render: r => <span className={`inline-flex rounded-md px-2 py-0.5 text-xs ${r.type === "Inbound" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{r.type}</span> },
            { key: "warehouse", header: "Warehouse" },
            { key: "date", header: "Date" },
            { key: "lines", header: "Lines", align: "right" },
            { key: "status", header: "Status", render: r => <span className="inline-flex rounded-md px-2 py-0.5 text-xs bg-secondary">{r.status}</span> },
          ] as Column<OpRow>[]}
          data={rows}
          rowKey={r => r.id}
          searchKeys={["ref", "warehouse", "type"]}
        />
      </Card2>
    </div>
  );
}
